<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ScheduledMessagesCreateForm from './ScheduledMessagesCreateForm.vue';

const store = useStore();
const { t } = useI18n();
const alert = useAlert;

const uiFlags = computed(() => store.getters['accountScheduledMessages/getUIFlags']);
const scheduledMessages = computed(() => store.getters['accountScheduledMessages/getAll']);
const isLoading = computed(() => uiFlags.value.isFetching);

const filterStatus = ref('pending');
const filterContact = ref('');
const showForm = ref(false);

const filteredMessages = computed(() => {
  let messages = scheduledMessages.value;
  if (!Array.isArray(messages)) return [];
  if (filterStatus.value !== 'all') {
    messages = messages.filter(m => m.status === filterStatus.value);
  }
  if (filterContact.value) {
    const q = filterContact.value.toLowerCase();
    messages = messages.filter(m =>
      m.contact_name?.toLowerCase().includes(q) ||
      m.contact_phone?.includes(q)
    );
  }
  return messages;
});

const stats = computed(() => {
  const all = Array.isArray(scheduledMessages.value) ? scheduledMessages.value : [];
  return {
    total: all.length,
    pending: all.filter(m => m.status === 'pending').length,
    sent: all.filter(m => m.status === 'sent').length,
    draft: all.filter(m => m.status === 'draft').length,
    failed: all.filter(m => m.status === 'failed').length,
  };
});

const formatDate = dateStr => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const statusColor = status => {
  const map = {
    pending: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    sent: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    draft: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  };
  return map[status] || map.draft;
};

const statusLabel = status => {
  const map = {
    pending: t('SCHEDULED_MESSAGES.STATUS.PENDING'),
    sent: t('SCHEDULED_MESSAGES.STATUS.SENT'),
    draft: t('SCHEDULED_MESSAGES.STATUS.DRAFT'),
    failed: t('SCHEDULED_MESSAGES.STATUS.FAILED'),
  };
  return map[status] || status;
};

const handleDelete = async message => {
  try {
    await store.dispatch('accountScheduledMessages/delete', { id: message.id });
    alert(t('SCHEDULED_MESSAGES.DELETED_SUCCESS'));
  } catch {
    alert(t('SCHEDULED_MESSAGES.ERRORS.DELETE_FAILED'));
  }
};

const handleViewConversation = message => {
  const url = `/app/accounts/${window.location.pathname.split('/')[3]}/conversations/${message.display_id}`;
  window.open(url, '_blank');
};

const statusOptions = computed(() => [
  { value: 'all', label: t('SCHEDULED_MESSAGES_PAGE.FILTERS.ALL'), count: stats.value.total },
  { value: 'pending', label: t('SCHEDULED_MESSAGES.STATUS.PENDING'), count: stats.value.pending },
  { value: 'sent', label: t('SCHEDULED_MESSAGES.STATUS.SENT'), count: stats.value.sent },
  { value: 'draft', label: t('SCHEDULED_MESSAGES.STATUS.DRAFT'), count: stats.value.draft },
  { value: 'failed', label: t('SCHEDULED_MESSAGES.STATUS.FAILED'), count: stats.value.failed },
]);

onMounted(() => {
  store.dispatch('accountScheduledMessages/getAll');
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <!-- Header - same pattern as CampaignLayout -->
    <header class="sticky top-0 z-10 px-6">
      <div class="w-full max-w-5xl mx-auto">
        <div class="flex items-center justify-between w-full h-20 gap-2">
          <span class="text-heading-1 text-n-slate-12">
            {{ t('SCHEDULED_MESSAGES_PAGE.TITLE') }}
          </span>
          <button
            class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border border-n-primary-300 dark:border-n-primary-700 text-n-slate-12 hover:opacity-80 transition-all"
            style="background-color: rgba(0, 255, 29, 0.2)"
            @click="showForm = !showForm"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            {{ t('SCHEDULED_MESSAGES_PAGE.NEW_SCHEDULED_MESSAGE') }}
          </button>
        </div>
      </div>
    </header>

    <!-- Main content -->
    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-5xl mx-auto py-4">
        <!-- Create Form -->
        <div v-if="showForm" class="mb-6 p-4 bg-white dark:bg-n-surface-2 rounded-xl border border-n-divider dark:border-n-divider">
          <ScheduledMessagesCreateForm @close="showForm = false" />
        </div>

        <!-- Filters -->
        <div class="filters-row">
          <div class="filter-buttons">
            <button
              v-for="opt in statusOptions"
              :key="opt.value"
              class="px-3 py-1 text-sm rounded-md transition-all border"
              :class="filterStatus === opt.value
                ? 'text-n-slate-12 shadow-sm font-medium border-n-primary-300 dark:border-n-primary-700 bg-white dark:bg-n-surface-1'
                : 'text-n-text-light dark:text-n-text-muted hover:text-n-slate-12 border-transparent hover:border-n-divider dark:hover:border-n-divider bg-transparent'"
              :style="filterStatus === opt.value ? { backgroundColor: 'rgba(0, 255, 29, 0.2)' } : {}"
              @click="filterStatus = opt.value"
            >
              {{ opt.label }}
              <span class="ml-1 text-xs opacity-60">{{ opt.count }}</span>
            </button>
          </div>
          <input
            v-model="filterContact"
            type="text"
            :placeholder="t('SCHEDULED_MESSAGES_PAGE.FILTERS.CONTACT_PLACEHOLDER')"
            class="search-input"
          />
        </div>

        <!-- Loading -->
        <div v-if="isLoading" class="flex justify-center items-center py-10">
          <Spinner />
        </div>

        <!-- Empty State -->
        <div
          v-else-if="filteredMessages.length === 0"
          class="flex flex-col items-center justify-center py-16 text-center"
        >
          <div class="w-16 h-16 rounded-full bg-n-surface-2 dark:bg-n-surface-3 flex items-center justify-center mb-4">
            <svg class="w-8 h-8 text-n-text-light dark:text-n-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <p class="text-sm text-n-text-light dark:text-n-text-muted">
            {{ t('SCHEDULED_MESSAGES_PAGE.EMPTY') }}
          </p>
        </div>

        <!-- Messages List -->
        <div v-else class="space-y-2">
          <div
            v-for="message in filteredMessages"
            :key="message.id"
            class="flex items-center gap-4 p-4 bg-white dark:bg-n-surface-2 rounded-xl border border-n-divider dark:border-n-divider hover:border-n-primary-300 dark:hover:border-n-primary-700 transition-colors"
          >
            <!-- Avatar -->
            <div class="flex-shrink-0 w-10 h-10 rounded-full bg-n-primary-100 dark:bg-n-primary-900/30 flex items-center justify-center">
              <svg class="w-5 h-5 text-n-primary-600 dark:text-n-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>

            <!-- Content -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <span class="text-sm font-medium text-n-slate-12 truncate">
                  {{ message.contact_name || t('SCHEDULED_MESSAGES_PAGE.UNKNOWN_CONTACT') }}
                </span>
                <span class="text-xs text-n-text-light dark:text-n-text-muted">
                  {{ message.contact_phone }}
                </span>
                <span
                  class="px-2 py-0.5 text-xs rounded-full flex-shrink-0"
                  :class="statusColor(message.status)"
                >
                  {{ statusLabel(message.status) }}
                </span>
              </div>
              <p class="text-sm text-n-text-light dark:text-n-text-muted truncate">
                {{ message.content }}
              </p>
              <div class="flex items-center gap-3 mt-1 text-xs text-n-text-light dark:text-n-text-muted">
                <span>{{ formatDate(message.scheduled_at) }}</span>
                <span v-if="message.inbox_name">{{ message.inbox_name }}</span>
                <span v-if="message.author_name">{{ message.author_name }}</span>
                <span v-if="message.recurring_scheduled_message_id" class="text-n-primary-600 dark:text-n-primary-400">
                  {{ t('SCHEDULED_MESSAGES_PAGE.RECURRING') }}
                </span>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex items-center gap-1 flex-shrink-0">
              <button
                class="p-2 rounded-lg text-n-text-light dark:text-n-text-muted hover:bg-n-surface-3 dark:hover:bg-n-surface-3 transition-colors"
                :title="t('SCHEDULED_MESSAGES_PAGE.VIEW_CONVERSATION')"
                @click="handleViewConversation(message)"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </button>
              <button
                v-if="message.status !== 'sent' && message.status !== 'failed'"
                class="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                :title="t('SCHEDULED_MESSAGES_PAGE.DELETE')"
                @click="handleDelete(message)"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  </section>
</template>

<style scoped>
.filters-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.filters-row :deep(.filter-buttons) {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  height: 42px;
  border: 1px solid var(--color-n-divider);
  border-radius: 8px;
  background-color: var(--color-n-surface-2);
}

.filters-row :deep(.search-input) {
  flex: 1;
  max-width: 360px;
  height: 24px;
  margin: 0 !important;
  padding: 0 12px;
  border: 0;
  border-radius: 8px;
  font-size: 14px;
  background-color: white;
  color: var(--color-n-slate-12);
  outline: none;
}

.filters-row :deep(.search-input::placeholder) {
  color: var(--color-n-text-light);
}

.filters-row :deep(.search-input:focus) {
  outline: 2px solid var(--color-n-primary-300);
}
</style>
